import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Search, X, Loader2, User } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  on_hold: "bg-amber-50 text-amber-700",
  closed: "bg-gray-100 text-gray-600",
};
const URGENCY_STYLE = {
  crisis: "bg-red-50 text-red-700 border border-red-200",
  high: "bg-orange-50 text-orange-700",
  standard: "bg-njas-sage-tint text-njas-sage-hover",
  low: "bg-gray-50 text-gray-500",
};

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    setLoading(true);
    const params = status === "all" ? {} : { status_filter: status };
    return api.get("/clients", { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((c) =>
      [c.first_name, c.last_name, c.preferred_name, c.email, c.phone].filter(Boolean)
        .some((v) => v.toLowerCase().includes(s))
    );
  }, [q, rows]);

  return (
    <div className="fade-in" data-testid="clients-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">CRM</div>
          <h2 className="font-serif text-3xl text-njas-navy">Clients</h2>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-client-btn" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2.5 text-sm font-medium hover:bg-njas-navy-hover">
          <Plus className="w-4 h-4" /> New client
        </button>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-njas-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email…" data-testid="clients-search" className={`${inputCls} pl-9`} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="clients-status-filter" className={`${inputCls} max-w-[200px]`}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="on_hold">On hold</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      <div className="rounded-xl bg-white border border-[var(--njas-border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-njas-bg-alt text-njas-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Urgency</th>
                <th className="text-left px-5 py-3">Worker</th>
                <th className="text-left px-5 py-3">Justice status</th>
                <th className="text-left px-5 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-njas-muted">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-njas-muted">No clients yet. Click &quot;New client&quot; to add one.</td></tr>}
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-[var(--njas-border)] hover:bg-njas-bg-alt/40" data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/dashboard/clients/${c.id}`} className="flex items-center gap-3 text-njas-navy hover:underline">
                      <div className="w-8 h-8 rounded-full bg-njas-sage-tint text-njas-sage-hover flex items-center justify-center font-serif">
                        {(c.first_name || "?").slice(0, 1)}
                      </div>
                      <div>
                        <div className="font-medium">{c.first_name} {c.last_name}</div>
                        {c.preferred_name && <div className="text-xs text-njas-muted">Prefers: {c.preferred_name}</div>}
                      </div>
                    </Link>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[c.status] || "bg-gray-100 text-gray-600"}`}>
                      {(c.status || "active").replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium capitalize ${URGENCY_STYLE[c.urgency] || "bg-gray-50 text-gray-500"}`}>
                      {c.urgency || "standard"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-njas-muted">{c.assigned_worker_name || "—"}</td>
                  <td className="px-5 py-3 text-njas-muted capitalize">{(c.justice_status || "—").replace(/_/g, " ")}</td>
                  <td className="px-5 py-3 text-njas-muted">{c.updated_at ? new Date(c.updated_at).toLocaleDateString("en-AU") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewClientModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewClientModal({ onClose, onCreated }) {
  const [staff, setStaff] = useState([]);
  const [f, set] = useState({
    first_name: "", last_name: "", preferred_name: "", date_of_birth: "",
    gender: "", pronouns: "", cultural_identity: "",
    phone: "", email: "", address: "",
    justice_status: "", status: "active", urgency: "standard",
    assigned_worker_id: "", background: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/staff/list").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f };
      Object.keys(body).forEach((k) => { if (body[k] === "") body[k] = null; });
      body.first_name = f.first_name; body.last_name = f.last_name;
      body.status = f.status; body.urgency = f.urgency;
      const res = await api.post("/clients", body);
      toast.success("Client created");
      onCreated(res.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose} data-testid="new-client-modal">
      <div className="bg-white rounded-2xl p-8 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="eyebrow text-njas-sage-hover text-[10px]">CRM</div>
            <h3 className="font-serif text-2xl text-njas-navy">New client</h3>
          </div>
          <button onClick={onClose} className="p-1 text-njas-muted hover:text-njas-navy"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="new-client-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First name" required><input required value={f.first_name} onChange={upd("first_name")} className={inputCls} data-testid="cf-first-name" /></Field>
            <Field label="Last name" required><input required value={f.last_name} onChange={upd("last_name")} className={inputCls} data-testid="cf-last-name" /></Field>
            <Field label="Preferred name"><input value={f.preferred_name} onChange={upd("preferred_name")} className={inputCls} /></Field>
            <Field label="Date of birth"><input type="date" value={f.date_of_birth} onChange={upd("date_of_birth")} className={inputCls} /></Field>
            <Field label="Gender"><input value={f.gender} onChange={upd("gender")} className={inputCls} /></Field>
            <Field label="Pronouns"><input value={f.pronouns} onChange={upd("pronouns")} className={inputCls} placeholder="she/her, he/him, they/them…" /></Field>
            <Field label="Cultural identity"><input value={f.cultural_identity} onChange={upd("cultural_identity")} className={inputCls} /></Field>
            <Field label="Phone"><input value={f.phone} onChange={upd("phone")} className={inputCls} /></Field>
            <Field label="Email"><input type="email" value={f.email} onChange={upd("email")} className={inputCls} /></Field>
            <Field label="Address"><input value={f.address} onChange={upd("address")} className={inputCls} /></Field>
            <Field label="Justice status">
              <select value={f.justice_status} onChange={upd("justice_status")} className={inputCls}>
                <option value="">—</option>
                <option value="incarcerated">Incarcerated</option>
                <option value="on_bail">On bail</option>
                <option value="on_parole">On parole</option>
                <option value="on_community_order">On community order</option>
                <option value="released">Released</option>
                <option value="at_risk">At risk</option>
                <option value="family_member">Family member</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Assigned worker">
              <select value={f.assigned_worker_id} onChange={upd("assigned_worker_id")} className={inputCls}>
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={f.status} onChange={upd("status")} className={inputCls}>
                <option value="active">Active</option>
                <option value="on_hold">On hold</option>
                <option value="closed">Closed</option>
              </select>
            </Field>
            <Field label="Urgency">
              <select value={f.urgency} onChange={upd("urgency")} className={inputCls}>
                <option value="low">Low</option>
                <option value="standard">Standard</option>
                <option value="high">High</option>
                <option value="crisis">Crisis</option>
              </select>
            </Field>
          </div>
          <Field label="Background / intake summary"><textarea rows={4} value={f.background} onChange={upd("background")} className={inputCls} /></Field>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} data-testid="new-client-submit" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm font-medium hover:bg-njas-navy-hover disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-njas-navy block mb-1.5 uppercase tracking-wider">{label}{required && <span className="text-njas-sage ml-1">*</span>}</span>
      {children}
    </label>
  );
}
